/* ═══════════════════════════════════════════════════════════════════
   COREVIAN UNIVERSITY — EXECUTIVE OVERSIGHT
   ───────────────────────────────────────────────────────────────────
   Two screens:
     1. renderExecutiveFunctionsPanel() — "Executive Functions" card
     2. renderAuditComplianceOversight() — "Audit / Compliance Oversight" card

   Both are presentation only. Neither computes new business rules —
   Executive Functions triggers the real existing sidebar buttons built
   by corevian-university-executive-operations.js; Audit/Compliance
   reads audit_events directly and states plainly what it does and
   does not currently cover, per explicit instruction not to claim
   completeness the database doesn't support.

   STATUS: NOT YET INTEGRATED. Not referenced by any .liquid template.
   ═══════════════════════════════════════════════════════════════════ */
(function () {
  'use strict';

  function esc(value) {
    return String(value ?? '').replace(/[&<>"']/g, c => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;'
    }[c]));
  }

  function renderExecutiveFunctionsPanel(container) {
    if (!container) return;

    if (typeof window.closeCorevianOperationsTracker === 'function') {
      window.closeCorevianOperationsTracker();
    }

    const candidates = [
      { id: 'navOperationsTracker', label: 'Operations Tracker', detail: 'Dispatch, clients, billing & operational control.' },
      { id: 'navComplianceDocumentControl', label: 'Compliance & Document Control', detail: 'Controlled document governance & administration.' }
    ];

    const available = candidates.filter(c => document.getElementById(c.id));

    if (!available.length) {
      container.innerHTML = `
        <div class="comp-notice">
          No executive operational functions are currently available for your account.
        </div>
      `;
      return;
    }

    container.innerHTML = `
      <div class="unit-header">
        <div class="unit-eyebrow">EXECUTIVE FUNCTIONS</div>
        <div class="unit-title">Executive Operational Workspace</div>
        <div class="unit-subtitle">
          Executive-level operational and administrative controls. This is not training content,
          and does not include the CEO Bible, which remains accessible only to the CEO directly.
        </div>
      </div>
      <div class="unit-grid">
        ${available.map(c => `
          <div class="comp-card" style="padding:16px;cursor:pointer" data-exec-passthrough="${esc(c.id)}">
            <div class="comp-code" style="color:#5ED4CF">${esc(c.label)}</div>
            <div class="comp-history" style="margin-top:6px">${esc(c.detail)}</div>
          </div>
        `).join('')}
      </div>
    `;

    container.querySelectorAll('[data-exec-passthrough]').forEach(el => {
      el.addEventListener('click', () => {
        const real = document.getElementById(el.getAttribute('data-exec-passthrough'));
        if (real) real.click();
      });
    });
  }

  window.renderExecutiveFunctionsPanel = renderExecutiveFunctionsPanel;

  const EVENT_TYPE_LABEL = {
    qualification_record_created: 'Record Created',
    qualification_record_updated: 'Record Updated',
    qualification_record_deleted: 'Record Deleted',
    qualification_record_changed: 'Record Changed'
  };

  const ENTITY_TABLE_LABEL = {
    competency_records: 'Competency Evaluation',
    field_training_records: 'Field Training Readiness',
    operational_authorizations: 'Operational Authorization',
    endorsements: 'Operational Endorsement',
    recertification_items: 'Recertification / Renewal'
  };

  async function loadAuditEvents(client, limit) {
    const result = await client
      .from('audit_events')
      .select('id,actor_user_id,target_user_id,event_type,entity_type,entity_id,event_payload,occurred_at')
      .order('occurred_at', { ascending: false })
      .limit(limit || 200);

    if (result.error) throw result.error;
    return result.data || [];
  }

  async function loadProfileNames(client, userIds) {
    const uniqueIds = Array.from(new Set(userIds.filter(Boolean)));
    if (!uniqueIds.length) return new Map();

    const result = await client
      .from('profiles')
      .select('user_id,full_name')
      .in('user_id', uniqueIds);

    if (result.error) return new Map();
    return new Map((result.data || []).map(p => [p.user_id, p.full_name]));
  }

  function renderEventRow(event, namesById) {
    const actorName = namesById.get(event.actor_user_id) || event.actor_user_id || 'Unknown';
    const targetName = namesById.get(event.target_user_id) || event.target_user_id || 'Unknown';
    const entityLabel = ENTITY_TABLE_LABEL[event.entity_type] || event.entity_type || 'Unknown Record';
    const eventLabel = EVENT_TYPE_LABEL[event.event_type] || event.event_type || 'Event';
    const when = event.occurred_at ? new Date(event.occurred_at).toLocaleString() : '—';

    return `
      <div class="comp-card" style="padding:14px;margin-top:10px">
        <div style="display:flex;justify-content:space-between;gap:12px;flex-wrap:wrap;align-items:baseline">
          <div style="font-size:13px;font-weight:800;color:var(--white)">${esc(entityLabel)} — ${esc(eventLabel)}</div>
          <div class="comp-history">${esc(when)}</div>
        </div>
        <div class="comp-history" style="margin-top:6px">
          <strong style="color:var(--white)">Actor:</strong> ${esc(actorName)}
          &nbsp;·&nbsp;
          <strong style="color:var(--white)">Affected Employee:</strong> ${esc(targetName)}
        </div>
      </div>
    `;
  }

  async function renderAuditComplianceOversight(container) {
    if (!container) return;

    container.innerHTML = `
      <div class="unit-header">
        <div class="unit-eyebrow">AUDIT / COMPLIANCE OVERSIGHT</div>
        <div class="unit-title">Who did what, when, and under what authority</div>
        <div class="unit-subtitle">
          This screen reads the controlled audit_events table directly. It is not a second
          source of truth for qualification status — see Workforce Readiness for that.
        </div>
      </div>
      <div class="comp-notice" style="border-color:rgba(251,191,36,.35)">
        <strong style="color:#FBBF24">Coverage, stated plainly:</strong>
        This trail currently covers competency evaluations, field training readiness,
        operational authorizations, endorsements, and recertification/renewal changes only.
        <strong>Controlled-document actions (create, approve, assign, acknowledge) are not
        yet audited</strong> — this section will be updated when that changes. Nothing here
        should be treated as complete or immutable coverage until that is independently verified
        against the live database.
      </div>
      <div class="comp-notice" style="margin-top:12px">Loading audit history…</div>
    `;

    try {
      const client =
        (typeof window.getSupabaseClient === 'function') ? window.getSupabaseClient() :
        (typeof getSupabaseClient === 'function') ? getSupabaseClient() :
        null;
      if (!client) {
        throw new Error('Supabase client is unavailable.');
      }
      const events = await loadAuditEvents(client, 200);

      const userIds = [];
      events.forEach(e => { userIds.push(e.actor_user_id, e.target_user_id); });
      const namesById = await loadProfileNames(client, userIds);

      const body = document.createElement('div');
      body.innerHTML = events.length
        ? events.map(e => renderEventRow(e, namesById)).join('')
        : `<div class="comp-notice" style="margin-top:12px">
             No audit events are currently recorded. This may mean nothing controlled has
             happened yet under the audited tables, or that audit installation is still in
             progress — this screen cannot distinguish those two cases on its own.
           </div>`;

      const loadingNotice = container.querySelector('.comp-notice:last-child');
      if (loadingNotice) loadingNotice.replaceWith(body);

    } catch (error) {
      const loadingNotice = container.querySelector('.comp-notice:last-child');
      const errorHtml = `
        <div class="comp-error" style="margin-top:12px">
          ${esc(error?.message || 'Unable to load audit history.')}
        </div>
      `;
      if (loadingNotice) {
        loadingNotice.outerHTML = errorHtml;
      } else {
        container.insertAdjacentHTML('beforeend', errorHtml);
      }
    }
  }

  window.renderAuditComplianceOversight = renderAuditComplianceOversight;

  async function renderExecutiveExceptionsRisk(container) {
    if (!container) return;

    // FIX — Executive Demo must use simulated workforce data only.
    // Mirrors the exact guard renderWorkforceReadiness() already uses
    // in corevian-university-2.js. Without this, this screen
    // unconditionally called loadWorkforceReadinessWorkspace() below —
    // the real production data loader — even while Demo Mode was
    // active. There is no real workforce data to load during a demo,
    // which is why the panel rendered empty (or failed silently)
    // regardless of what the other four Quick Access panels did.
    if (window.demoMode && typeof window.renderDemoExceptionsRisk === 'function') {
      window.renderDemoExceptionsRisk(container);
      return;
    }

    if (typeof window.explainQualificationStatus !== 'function') {
      container.innerHTML = `
        <div class="comp-error">
          The qualification explanation engine is not loaded — this screen cannot run without it.
        </div>
      `;
      return;
    }

    container.innerHTML = `<div class="comp-notice">Loading workforce readiness records…</div>`;

    let rows;
    try {
      if (typeof loadWorkforceReadinessWorkspace === 'function') {
        rows = await loadWorkforceReadinessWorkspace();
      } else {
        rows = (typeof workforceReadinessRows !== 'undefined') ? workforceReadinessRows : [];
      }
    } catch (error) {
      container.innerHTML = `
        <div class="comp-error">
          ${esc(error?.message || 'Unable to load workforce readiness records.')}
        </div>
      `;
      return;
    }

    if (!rows || !rows.length) {
      container.innerHTML = `
        <div class="comp-notice">
          No active employee workforce records were found.
        </div>
      `;
      return;
    }

    const exceptions = rows
      .map(row => ({ row, explanation: window.explainQualificationStatus(row) }))
      .filter(x => !x.explanation.authorized || x.explanation.warnings.length > 0);

    container.innerHTML = `
      <div class="unit-header">
        <div class="unit-eyebrow">EXCEPTIONS &amp; OPERATIONAL RISK</div>
        <div class="unit-title">What currently requires leadership attention</div>
        <div class="unit-subtitle">
          Every item below is generated from the same Workforce Readiness data and the same
          qualification explanation engine used elsewhere — nothing here is computed independently.
          Service-scope/endorsement conflicts are not yet detected by any existing function and are
          intentionally not included in this first version.
        </div>
      </div>
      <div class="comp-notice">
        ${exceptions.length} employee${exceptions.length === 1 ? '' : 's'} currently require attention,
        out of ${rows.length} total.
      </div>
      ${
        exceptions.length
          ? exceptions.map(({ row, explanation }) => {
              const color = explanation.authorized ? '#FBBF24' : '#FCA5A5';
              const items = [...explanation.blockers, ...explanation.warnings];
              return `
                <div class="comp-card" style="padding:16px;margin-top:12px;border-left:3px solid ${color}">
                  <div style="display:flex;justify-content:space-between;gap:12px;flex-wrap:wrap;align-items:baseline">
                    <div style="font-size:15px;font-weight:800;color:var(--white)">${esc(row.full_name || 'Unnamed Employee')}</div>
                    <div style="font-size:11px;font-weight:800;color:${color}">${explanation.authorized ? 'ACTION REQUIRED' : 'NOT AUTHORIZED'}</div>
                  </div>
                  <div class="comp-history" style="margin-top:6px">${esc(explanation.summary)}</div>
                  ${items.map(item => `
                    <div class="comp-history" style="margin-top:6px">
                      <strong style="color:var(--white)">${esc(item.control)}:</strong> ${esc(item.explanation)}
                      — <em>${esc(item.nextAction)}</em>
                    </div>
                  `).join('')}
                </div>
              `;
            }).join('')
          : `<div class="comp-notice" style="margin-top:12px">No current exceptions — every workforce member is fully ready.</div>`
      }
    `;
  }

  window.renderExecutiveExceptionsRisk = renderExecutiveExceptionsRisk;
})();